import { Flex } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { useQuery } from '@tanstack/react-query';
import { getFileReactQuery } from '@dealstack/frontend/shared/dataExchange/services/mip.service';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import { PdfViewer } from '@dealstack/frontend/shared/components/DocumentViewerV2/PdfViewer/PdfViewer';
import { useState, useEffect, ComponentProps, ReactNode } from 'react';
import styled from 'styled-components';
import { SigningPanel } from './SigningPanel';

const PdfPane = styled.div<{ $isStacked: boolean }>`
	flex: ${({ $isStacked }) => ($isStacked ? 'none' : '1 1 auto')};
	height: ${({ $isStacked }) => ($isStacked ? '55vh' : '100%')};
	min-width: ${({ $isStacked }) => ($isStacked ? 'auto' : '640px')};
	overflow: hidden;
	padding-left: 1rem;
	padding-right: 1rem;
	padding-bottom: 1rem;
	display: flex;
	flex-direction: column;
	min-height: 0;

	.react-pdf__Page {
		box-shadow:
			0 1px 2px rgba(15, 23, 42, 0.08),
			0 4px 12px rgba(15, 23, 42, 0.06);
		border-radius: 4px;
		overflow: hidden;
		margin: 0 auto;
	}

	/* Hide the PDF scroll track but keep wheel / drag scrolling */
	* {
		scrollbar-width: none;
		-ms-overflow-style: none;
	}
	*::-webkit-scrollbar {
		width: 0;
		height: 0;
		display: none;
	}
`;

interface Props extends ComponentProps<typeof SigningPanel> {
	fileId: string;
	fileName: string;
	/** When provided, replaces the eID Easy panel content (the PDF pane stays). */
	panelOverride?: ReactNode;
	/** Optional content rendered above the panel content; ignored while panelOverride is active. */
	panelHeader?: ReactNode;
}

export const EidEasySigningSurface = ({
	fileId,
	fileName,
	panelOverride,
	panelHeader,
	...panelProps
}: Props) => {
	const isStacked = useMediaQuery('(max-width: 768px)') ?? false;

	const { data: pdfResponse, isLoading } = useQuery({
		queryKey: [API_PATHS.FILE_ID_GET, { id: fileId }],
		queryFn: ({ signal }) => getFileReactQuery({ id: fileId }, signal),
		enabled: !!fileId,
	});

	const [pdfBlob, setPdfBlob] = useState<Blob | undefined>(undefined);

	useEffect(() => {
		if (!pdfResponse) {
			setPdfBlob(undefined);
			return;
		}
		void pdfResponse.blob().then(setPdfBlob);
	}, [pdfResponse]);

	return (
		<Flex
			direction={isStacked ? 'column' : 'row'}
			gap={0}
			w="100%"
			h="100%"
			style={{ minHeight: isStacked ? '70vh' : '100%' }}
		>
			<PdfPane data-testid="signing-surface-pdf-pane" $isStacked={isStacked}>
				<PdfViewer
					fileName={fileName}
					file={pdfBlob}
					isLoading={isLoading}
					showBackButton={false}
				/>
			</PdfPane>
			<div
				data-testid="signing-surface-panel-pane"
				style={{
					flex: isStacked ? 'none' : '0 0 380px',
					paddingBottom: '1.25rem',
					overflowY: 'auto',
					border: `1.5px solid #e5e7eb`,
					borderRadius: '10px',
					marginRight: '1rem',
					marginBottom: '1rem',
				}}
			>
				{panelOverride ?? (
					<>
						{panelHeader}
						<SigningPanel {...panelProps} />
					</>
				)}
			</div>
		</Flex>
	);
};
